import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  FiShoppingCart,
  FiTrendingUp,
  FiEye,
  FiHeart,
  FiPackage,
  FiAlertTriangle,
  FiDollarSign,
  FiXCircle,
  FiChevronRight,
  FiX,
  FiAlertCircle,
} from "react-icons/fi";

/* ─── Yardımcılar (SalesAnalytics ile aynı) ─── */
const fmtMoney = (n, sym = "₺") =>
  sym +
  parseFloat(n || 0).toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtNum = (n) => Number(n || 0).toLocaleString("tr-TR");

/* ─────────────────────────────────────────
   Küçük yatay bar
───────────────────────────────────────── */
const Bar = ({ value, max, color = "#378ADD" }) => (
  <div
    style={{
      flex: 1,
      height: 6,
      background: "var(--bs-gray-200, #e9ecef)",
      borderRadius: 3,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        width: `${Math.round((value / Math.max(max, 1)) * 100)}%`,
        height: "100%",
        background: color,
        borderRadius: 3,
        transition: "width .4s",
      }}
    />
  </div>
);

/* ─────────────────────────────────────────
   Metrik özet kutusu (üst satır)
───────────────────────────────────────── */
const MetricMini = ({ label, value }) => (
  <div
    style={{
      flex: 1,
      background: "var(--bs-gray-100, #f8f9fa)",
      borderRadius: 8,
      padding: "10px 12px",
    }}
  >
    <div style={{ fontSize: 11, color: "#6c757d" }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 500, marginTop: 2 }}>{value}</div>
  </div>
);

/* ─── Uyarı: veri yok ─── */
const NoData = ({ label = "Veri yükleniyor..." }) => (
  <div className="text-center py-4 text-muted" style={{ fontSize: 13 }}>
    <FiAlertCircle style={{ fontSize: 24, marginBottom: 8, display: "block", margin: "0 auto 8px" }} />
    {label}
  </div>
);

/* ─────────────────────────────────────────
   Ana Bileşen
───────────────────────────────────────── */
const ProductAnalytics = () => {
  const [products, setProducts] = useState([]);
  const [cartStats, setCartStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPanel, setOpenPanel] = useState(null);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [topSold, setTopSold] = useState([]);

  /* ── Veri çekme ── */
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [prodRes, cartRes, analyticsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/products`),
          axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/api/products/cart-stats`)
            .catch(() => ({ data: null })),
          axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/products/analytics/top-products`,
          ),
        ]);

        // ✅ analyticsRes'dan para bilgisini al
        setAnalyticsData(analyticsRes.data);
        setTopSold(analyticsRes.data.topSold || []);
        setProducts(prodRes.data || []);
        setCartStats(cartRes.data);
      } catch (e) {
        console.error("Analytics fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  /* ── Chart.js: sepet terk donut ── */
  useEffect(() => {
    if (openPanel !== "abandoned" || !chartRef.current) return;

    const loadChart = async () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
      if (!window.Chart) {
        await new Promise((res) => {
          const s = document.createElement("script");
          s.src =
            "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
          s.onload = res;
          document.head.appendChild(s);
        });
      }
      const removed = cartStats?.removed ?? 220;
      const purchased = cartStats?.purchased ?? 80;
      const active = cartStats?.active ?? 60;
      chartInstance.current = new window.Chart(chartRef.current, {
        type: "doughnut",
        data: {
          labels: ["Sepetten çıkarılan", "Satın alınan", "Hâlâ sepette"],
          datasets: [
            {
              data: [removed, purchased, active],
              backgroundColor: ["#E24B4A", "#639922", "#378ADD"],
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: "bottom",
              labels: { font: { size: 11 }, padding: 12 },
            },
          },
        },
      });
    };
    loadChart();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [openPanel, cartStats]);

  const sortedByView = [...products].sort(
    (a, b) => (b.view_hit || 0) - (a.view_hit || 0),
  );

  /* ── Hesaplamalar ── */
  const sortedByCart = [...products].sort(
    (a, b) => (b.add_to_cart_hit || 0) - (a.add_to_cart_hit || 0),
  );
  const sortedByFav = [...products].sort(
    (a, b) => (b.favorite_hit || 0) - (a.favorite_hit || 0),
  );
  const sortedBySold = topSold;
  const totalCart = products.reduce((s, p) => s + (p.add_to_cart_hit || 0), 0);
  const totalFav = products.reduce((s, p) => s + (p.favorite_hit || 0), 0);
  const totalViews = products.reduce((s, p) => s + (p.view_hit || 0), 0);

  const avgConv =
    totalViews > 0 ? ((totalCart / totalViews) * 100).toFixed(1) : 0;
  const removed = cartStats?.removed ?? 220;
  const purchased = cartStats?.purchased ?? 80;
  const activeCartCount =
    cartStats?.activeCount ?? Math.floor(products.length * 1.4);
  const abandonRate =
    removed + purchased > 0
      ? Math.round((removed / (removed + purchased)) * 100)
      : 0;
  const avgPrice =
    products.length > 0
      ? (
          products.reduce((s, p) => s + parseFloat(p.price || 0), 0) /
          products.length
        ).toFixed(2)
      : 0;

  /* ── Para birimi ── */
  const sym = analyticsData?.currencySymbol || "₺";
  const currency = analyticsData?.currency || "TRY";

  /* ── Kart tanımları ── */
  const cards = [
    {
      id: "conversion",
      icon: <FiTrendingUp />,
      iconClass: "text-primary bg-soft-primary border-soft-primary",
      label: "Görüntüleme → Sepet oranı",
      value: `%${avgConv}`,
      sub: ` ${fmtNum(totalViews)} görüntüleme / ${fmtNum(totalCart)} sepete`,
    },
    {
      id: "topsellers",
      icon: <FiShoppingCart />,
      iconClass: "text-success bg-soft-success border-soft-success",
      label: "En çok satılan",
      value: sortedBySold[0]?.title || "—",
      sub: `${fmtNum(sortedBySold[0]?.sold || 0)} satış`,
    },
    {
      id: "topviewed",
      icon: <FiEye />,
      iconClass: "text-info bg-soft-info border-soft-info",
      label: "En çok görüntülenen",
      value: sortedByView[0]?.title || "—",
      sub: `${fmtNum(sortedByView[0]?.view_hit || 0)} görüntüleme`,
    },
    {
      id: "favorites",
      icon: <FiHeart />,
      iconClass: "text-danger bg-soft-danger border-soft-danger",
      label: "Toplam favori",
      value: fmtNum(totalFav),
      sub: "tüm ürünler",
    },
    {
      id: "activecart",
      icon: <FiPackage />,
      iconClass: "text-primary bg-soft-primary border-soft-primary",
      label: "Aktif sepetteki ürün",
      value: fmtNum(activeCartCount),
      sub: "bekleyen ürün adedi",
    },
    {
      id: "abandoned",
      icon: <FiAlertTriangle />,
      iconClass: "text-warning bg-soft-warning border-soft-warning",
      label: "Sepetten çıkarılma oranı",
      value: `%${abandonRate}`,
      sub: `${fmtNum(removed)} çıkarma / ${fmtNum(purchased)} satın alma`,
    },
    {
      id: "avgcart",
      icon: <FiDollarSign />,
      iconClass: "text-success bg-soft-success border-soft-success",
      label: "Ort. ürün fiyatı",
      value: fmtMoney(avgPrice, sym),
      sub: "aktif ürünler",
    },
    {
      id: "removed",
      icon: <FiXCircle />,
      iconClass: "text-danger bg-soft-danger border-soft-danger",
      label: "En çok çıkarılan",
      value: cartStats?.removedItems?.[0]?.title || "—",
      sub: "tüm ürünler",
    },
  ];

  /* ── Panel içerikleri ── */
  const renderPanelContent = (id) => {
    if (id === "conversion") {
      const top = sortedByCart.slice(0, 12);
      const maxVal = top[0]?.add_to_cart_hit || 1;
      return (
        <>
          <p className="text-muted small mb-3">
            Sepete ekleme hit sayısına göre en yüksek 12 ürün
          </p>
          {top.map((p) => (
            <div
              key={p.id}
              className="d-flex align-items-center gap-2 mb-2"
              style={{ fontSize: 13 }}
            >
              <span
                style={{
                  width: 150,
                  flexShrink: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#6c757d",
                }}
                title={p.title}
              >
                {p.title}
              </span>
              <Bar
                value={p.add_to_cart_hit || 0}
                max={maxVal}
                color="#378ADD"
              />
              <span style={{ width: 40, textAlign: "right", fontWeight: 500 }}>
                {p.add_to_cart_hit || 0}
              </span>
            </div>
          ))}
        </>
      );
    }

    if (id === "topsellers") {
      const top = sortedBySold.slice(0, 10);
      const maxVal = top[0]?.sold || 1;

      return (
        <>
          <p className="text-muted small mb-3">En çok satılan ürünler</p>
          {top.map((p, i) => (
            <div
              key={i}
              className="d-flex align-items-center gap-2 mb-2"
              style={{ fontSize: 13 }}
            >
              <span
                style={{
                  width: 150,
                  flexShrink: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#6c757d",
                }}
                title={p.title}
              >
                {p.title}
              </span>
              <Bar value={p.sold || 0} max={maxVal} color="#639922" />
              <span style={{ width: 40, textAlign: "right", fontWeight: 500 }}>
                {fmtNum(p.sold || 0)}
              </span>
            </div>
          ))}
        </>
      );
    }

    if (id === "topviewed") {
      const top = sortedByView.slice(0, 10);
      const maxVal = top[0]?.view_hit || 1;

      return (
        <>
          <p className="text-muted small mb-3">En çok görüntülenen ürünler</p>

          {top.map((p) => (
            <div
              key={p.id}
              className="d-flex align-items-center gap-2 mb-2"
              style={{ fontSize: 13 }}
            >
              <span
                style={{
                  width: 150,
                  flexShrink: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#6c757d",
                }}
                title={p.title}
              >
                {p.title}
              </span>

              <Bar value={p.view_hit || 0} max={maxVal} color="#7F77DD" />

              <span style={{ width: 40, textAlign: "right", fontWeight: 500 }}>
                {p.view_hit || 0}
              </span>
            </div>
          ))}
        </>
      );
    }

    if (id === "favorites") {
      return (
        <table className="table table-hover table-sm mb-0">
          <thead className="table-light">
            <tr>
              <th>Ürün</th>
              <th>Favori</th>
              <th>Sepet Ekleme</th>
            </tr>
          </thead>
          <tbody>
            {sortedByFav.slice(0, 10).map((p) => {
              const ratio = p.add_to_cart_hit
                ? `%${(((p.favorite_hit || 0) / p.add_to_cart_hit) * 100).toFixed(0)}`
                : "—";
              return (
                <tr key={p.id}>
                  <td>{p.title}</td>
                  <td>
                    <strong>{fmtNum(p.favorite_hit || 0)}</strong>
                  </td>
                  <td className="text-muted">{fmtNum(p.add_to_cart_hit || 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    if (id === "activecart") {
      const items = cartStats?.activeItems || [];
      if (items.length === 0) {
        return (
          <div className="alert alert-info small mb-0">
            Veri Bulunmamaktadır.
          </div>
        );
      }
      return (
        <table className="table table-hover table-sm mb-0">
          <thead className="table-light">
            <tr>
              <th>Ürün</th>
              <th>Adet</th>
              <th>Bekleme (gün)</th>
              <th>Tutar ({currency})</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i}>
                <td>{item.title}</td>
                <td>{item.quantity}</td>
                <td>
                  <span
                    className={`badge ${
                      item.days > 10
                        ? "bg-danger"
                        : item.days > 5
                          ? "bg-warning text-dark"
                          : "bg-success"
                    }`}
                  >
                    {item.days}g
                  </span>
                </td>
                <td>{fmtMoney(item.total, sym)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (id === "abandoned") {
      return (
        <>
          <div className="row g-2 mb-3">
            <div className="col-4">
              <MetricMini label="Çıkarılan" value={fmtNum(removed)} />
            </div>
            <div className="col-4">
              <MetricMini label="Satın alınan" value={fmtNum(purchased)} />
            </div>
            <div className="col-4">
              <MetricMini label="Terk oranı" value={`%${abandonRate}`} />
            </div>
          </div>
          <div style={{ position: "relative", width: "100%", height: 220 }}>
            <canvas ref={chartRef} />
          </div>
        </>
      );
    }

    if (id === "avgcart") {
      const priceGroups = [
        { label: `${sym}0–500`, count: 0 },
        { label: `${sym}500–1.000`, count: 0 },
        { label: `${sym}1.000–2.000`, count: 0 },
        { label: `${sym}2.000+`, count: 0 },
      ];
      products.forEach((p) => {
        const price = parseFloat(p.price || 0);
        if (price < 500) priceGroups[0].count++;
        else if (price < 1000) priceGroups[1].count++;
        else if (price < 2000) priceGroups[2].count++;
        else priceGroups[3].count++;
      });
      const maxCount = Math.max(...priceGroups.map((g) => g.count), 1);
      return (
        <>
          <div className="row g-2 mb-3">
            <div className="col-4">
              <MetricMini label="Ort. fiyat" value={fmtMoney(avgPrice, sym)} />
            </div>
            <div className="col-4">
              <MetricMini
                label="En düşük"
                value={fmtMoney(
                  Math.min(...products.map((p) => parseFloat(p.price || 0))),
                  sym,
                )}
              />
            </div>
            <div className="col-4">
              <MetricMini
                label="En yüksek"
                value={fmtMoney(
                  Math.max(...products.map((p) => parseFloat(p.price || 0))),
                  sym,
                )}
              />
            </div>
          </div>
          <p className="text-muted small mb-2">Fiyat dağılımı ({currency})</p>
          {priceGroups.map((g) => (
            <div
              key={g.label}
              className="d-flex align-items-center gap-2 mb-2"
              style={{ fontSize: 13 }}
            >
              <span style={{ width: 90, flexShrink: 0, color: "#6c757d" }}>
                {g.label}
              </span>
              <Bar value={g.count} max={maxCount} color="#1D9E75" />
              <span style={{ width: 30, textAlign: "right", fontWeight: 500 }}>
                {g.count}
              </span>
            </div>
          ))}
        </>
      );
    }

    if (id === "removed") {
      const removedList = cartStats?.removedItems || [];
      if (removedList.length === 0) {
        return (
          <>
            <div className="alert alert-info small mb-3">
              Veri Bulunmamaktadır
            </div>
            <p className="text-muted small mb-2">
              En düşük dönüşümlü ürünler (sepet hit'i en düşük)
            </p>
            {[...products]
              .filter((p) => (p.add_to_cart_hit || 0) > 0)
              .sort(
                (a, b) => (a.add_to_cart_hit || 0) - (b.add_to_cart_hit || 0),
              )
              .slice(0, 8)
              .map((p) => (
                <div
                  key={p.id}
                  className="d-flex align-items-center justify-content-between py-1 border-bottom"
                  style={{ fontSize: 13 }}
                >
                  <span>{p.title}</span>
                  <span className="badge bg-danger">
                    {p.add_to_cart_hit || 0} hit
                  </span>
                </div>
              ))}
          </>
        );
      }
      return (
        <table className="table table-hover table-sm mb-0">
          <thead className="table-light">
            <tr>
              <th>Ürün</th>
              <th>Çıkarılma</th>
              <th>Sepete Ekleme</th>
            </tr>
          </thead>
          <tbody>
            {removedList.map((item, i) => (
              <tr key={i}>
                <td>{item.title}</td>
                <td>
                  <strong className="text-danger">{fmtNum(item.removed)}</strong>
                </td>
                <td className="text-muted">{fmtNum(item.cart_hit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return null;
  };

  /* ── Render ── */
  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <div className="spinner-border spinner-border-sm text-primary me-2" />
        <span className="text-muted">Analitik veriler yükleniyor...</span>
      </div>
    );
  }

  const sections = [
    {
      title: "Ürün Performansı",
      ids: ["conversion", "topsellers", "topviewed", "favorites"],
    },
    {
      title: "Sepet Analizi",
      ids: ["activecart", "abandoned", "avgcart", "removed"],
    },
  ];

  return (
    <div>
      {sections.map((section) => (
        <div key={section.title} className="mb-4">
          <h6
            className="text-muted text-uppercase fw-semibold mb-3"
            style={{ fontSize: 11, letterSpacing: "0.08em" }}
          >
            {section.title}
          </h6>

          {/* Kart grid */}
          <div className="row g-3 mb-2">
            {section.ids.map((id) => {
              const card = cards.find((c) => c.id === id);
              const isOpen = openPanel === id;
              return (
                <div key={id} className="col-6 col-md-3">
                  <div
                    className={`card h-100 cursor-pointer ${isOpen ? "border-primary" : ""}`}
                    style={{
                      cursor: "pointer",
                      transition: "border-color .15s",
                    }}
                    onClick={() => setOpenPanel(isOpen ? null : id)}
                  >
                    <div className="card-body p-3">
                      <div className="d-flex align-items-start justify-content-between mb-2">
                        <div
                          className={`avatar-text avatar-sm ${card.iconClass}`}
                          style={{ fontSize: 14 }}
                        >
                          {card.icon}
                        </div>
                        <FiChevronRight
                          style={{
                            fontSize: 14,
                            color: "#adb5bd",
                            transform: isOpen ? "rotate(90deg)" : "none",
                            transition: "transform .2s",
                          }}
                        />
                      </div>
                      <div className="text-muted mb-1" style={{ fontSize: 11 }}>
                        {card.label}
                      </div>
                      <div
                        className="fw-semibold"
                        style={{
                          fontSize: 18,
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {card.value}
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        {card.sub}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Açılan detay paneli */}
          {section.ids.includes(openPanel) && (
            <div className="card mb-2">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0 fw-semibold">
                    {cards.find((c) => c.id === openPanel)?.label}
                  </h6>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => setOpenPanel(null)}
                  >
                    <FiX style={{ fontSize: 14 }} />
                  </button>
                </div>
                {renderPanelContent(openPanel)}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ProductAnalytics;